import { useState } from 'react';

import { Box, Heading, Modal } from '@zillow/constellation-10';

import { VERIFICATION_STEPS } from './constants';

import AddPhoneNumberOrEmail from './components/AddPhoneNumberOrEmail';
import Finished from './components/Finished';
import GetVerificationCode from './components/GetVerificationCode';
import VerificationCodeSent from './components/VerificationCodeSent';

import type {
  GetUserPropertyDetailsResponseWebDto,
  PhoneVerificationSource,
} from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { MODES_VALUES } from './constants';

export type VerificationModalProps = {
  headerText: string;
  initialStep?: number;
  isContentEditable?: boolean;
  initialContentToVerify?: string;
  onVerificationConfirmation?: (contentValue: string) => Promise<void>;
  onContentValueAdded?: (inputValue: string) => Promise<void>;
  showVerificationModal: (value: boolean) => void;
  stepsConfig: Array<string>;
  verificationSuccessfulCTAText?: string;
  verificationSource: PhoneVerificationSource;
  verificationMode?: MODES_VALUES;
  listingDetails?: GetUserPropertyDetailsResponseWebDto;
};

/**
 * Phone verification modal using beta web API (no Redux).
 * Used by listing-claiming contact verification flow.
 */
const VerificationModal = ({
  headerText,
  initialStep = 0,
  isContentEditable = true,
  initialContentToVerify = '',
  onVerificationConfirmation,
  onContentValueAdded,
  showVerificationModal,
  stepsConfig,
  verificationSuccessfulCTAText = 'Return to listing',
  verificationSource,
  verificationMode = 'phone',
  listingDetails,
}: VerificationModalProps) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStep);
  const [verificationType, setVerificationType] = useState('');
  const [contentToVerify, setContentToVerify] = useState(initialContentToVerify);

  const incrementStep = (stepToJumpTo?: string) => {
    if (stepToJumpTo) {
      const stepToJumpToIndex = stepsConfig.findIndex((element) => element === stepToJumpTo);
      setCurrentStepIndex(stepToJumpToIndex >= 0 ? stepToJumpToIndex : currentStepIndex);
    } else {
      setCurrentStepIndex((i) => i + 1);
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && showVerificationModal(false)}
      useDismissProps={{ outsidePress: false }}
      size={{ base: 'fullScreen', lg: 'md' }}
      header={<Heading level={2}>{headerText}</Heading>}
      body={
        <Box
          data-testid="VerificationModal"
          css={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.ADD_CONTENT && (
            <AddPhoneNumberOrEmail
              currentStepIndex={currentStepIndex}
              incrementStep={incrementStep}
              saveContentToVerify={setContentToVerify}
              onContentValueAdded={onContentValueAdded}
              verificationMode={verificationMode}
              listingDetails={listingDetails}
            />
          )}
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.GET_VERIFICATION_CODE && (
            <GetVerificationCode
              currentStepIndex={currentStepIndex}
              incrementStep={incrementStep}
              isContentEditable={isContentEditable}
              contentValue={contentToVerify}
              saveVerificationType={setVerificationType}
              verificationSource={verificationSource}
            />
          )}
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.VERIFICATION_CODE_SENT && (
            <VerificationCodeSent
              currentStepIndex={currentStepIndex}
              incrementStep={incrementStep}
              isContentEditable={isContentEditable}
              contentValue={contentToVerify}
              verificationType={verificationType}
              onVerificationConfirmation={onVerificationConfirmation}
              verificationSource={verificationSource}
              verificationMode={verificationMode}
            />
          )}
          {stepsConfig[currentStepIndex] === VERIFICATION_STEPS.VERIFICATION_SUCCESS && (
            <Finished
              showVerificationModal={showVerificationModal}
              verificationSuccessfulCTAText={verificationSuccessfulCTAText}
              verificationMode={verificationMode}
            />
          )}
        </Box>
      }
    />
  );
};

export { VerificationModal };
